import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import FormNumberField from '../../../components/ui/form/FormNumberField'
import FormTextareaField from '../../../components/ui/form/FormTextareaField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import {
  criteriaScoreRangeFormSchema,
  type CriteriaScoreRangeFormValues,
} from './criteriaScoreRangeFormSchema'
import type { CriteriaScoreRange, CriteriaScoreRangeUpdate } from '../types'

/** Düzenleme yükü (modDate hariç — sayfa optimistic-lock için ekler). */
export type CriteriaScoreRangeFormPayload = Omit<CriteriaScoreRangeUpdate, 'modDate'>

interface Props {
  visible: boolean
  initial: CriteriaScoreRange | null
  submitting: boolean
  onSubmit: (payload: CriteriaScoreRangeFormPayload) => void
  onHide: () => void
}

/**
 * Değerlendirme Skoru düzenleme formu (027: react-hook-form + zod). Yalnız düzenleme (create yok). TR ad +
 * başlangıç + bitiş zorunlu; EN ad ve açıklamalar opsiyonel; `endScore > startScore` (şema superRefine).
 */
export default function CriteriaScoreRangeFormDialog({
  visible,
  initial,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset } = useZodForm(criteriaScoreRangeFormSchema, {
    defaultValues: {
      nameTr: '',
      nameEn: '',
      startScore: null,
      endScore: null,
      descriptionTr: '',
      descriptionEn: '',
    },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
        startScore: initial?.startScore ?? null,
        endScore: initial?.endScore ?? null,
        descriptionTr: initial?.descriptionTr ?? '',
        descriptionEn: initial?.descriptionEn ?? '',
      })
  }, [visible, initial, reset])

  const onValid = (v: CriteriaScoreRangeFormValues) =>
    onSubmit({
      nameTr: v.nameTr,
      nameEn: v.nameEn ? v.nameEn : null,
      // superRefine, gönderimde start/end'in null olmamasını garanti eder.
      startScore: v.startScore as number,
      endScore: v.endScore as number,
      descriptionTr: v.descriptionTr ? v.descriptionTr : null,
      descriptionEn: v.descriptionEn ? v.descriptionEn : null,
    })

  return (
    <FormDialog
      visible={visible}
      header={t('activity.criteriaScoreRange.form.editTitle')}
      submitLabel={t('activity.criteriaScoreRange.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField
          control={control}
          name="nameTr"
          label={t('activity.criteriaScoreRange.form.nameTr')}
          required
          autoFocus
        />
        <FormTextField
          control={control}
          name="nameEn"
          label={t('activity.criteriaScoreRange.form.nameEn')}
        />
        <FormNumberField
          control={control}
          name="startScore"
          label={t('activity.criteriaScoreRange.columns.startScore')}
          required
          min={0}
        />
        <FormNumberField
          control={control}
          name="endScore"
          label={t('activity.criteriaScoreRange.columns.endScore')}
          required
          min={0}
        />
        <FormTextareaField
          control={control}
          name="descriptionTr"
          label={t('activity.criteriaScoreRange.form.descriptionTr')}
          maxLength={1000}
        />
        <FormTextareaField
          control={control}
          name="descriptionEn"
          label={t('activity.criteriaScoreRange.form.descriptionEn')}
          maxLength={1000}
        />
      </div>
    </FormDialog>
  )
}
